import React, { useMemo, useEffect, useCallback, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  TableBody,
  TableContainer,
  TableCell,
  TableRow,
  Table,
  Switch,
} from "@mui/material";
import { DoubleArrow } from "@mui/icons-material";
import { CustomTextBox } from "components/CustomComponents";
import { useForm } from "react-hook-form";
import { useFetch } from "services/hooks/useFetch";
import { CustomButtonWithBorder } from "components/CustomComponents/CustomButton";
import { customAPI } from "services/utils/CustomAPI";
import {
  doubleArrowStyle,
  themes,
  parentBoxStyle,
  childBoxStyleWithColor,
  infoStyle,
  toAlignEnd,
  childBoxStyle,
  defaultAllSelection,
} from "services/constants";
import { useSelector } from "react-redux";
import { showToast } from "services/utils/Status";
import {
  onlyNumberRegex,
  atleastOneCharacterWithSpace,
} from "services/constants/patterns";
import { CustomAutoComplete } from "components/CustomComponents";

// Import reusable components
import { SalaryComponentTable } from "./SalaryComponentTable";
import { useSalaryComponents } from "./useSalaryComponents";
import { childBoxStyleSpaceBetween } from "services/constants";
import { CustomButton } from "components/CustomComponents";

const updateCrossCategoryComponents = (
  changedComponent,
  allComponentsMap,
  totalSalary
) => {
  const updatedCategories = {};
  let currentComponentsMap = { ...allComponentsMap };

  const getComponentValue = (code) => {
    if (!code) return 0;

    const upperCode = code.toUpperCase();

    if (upperCode === "GROSS") {
      return totalSalary;
    }

    for (const components of Object.values(updatedCategories)) {
      for (const component of components) {
        const componentCode =
          component.code?.toUpperCase() ||
          component.component_name?.code?.toUpperCase();

        if (componentCode === upperCode) {
          return parseFloat(component.amount) || 0;
        }
      }
    }

    for (const components of Object.values(currentComponentsMap)) {
      for (const component of components) {
        const componentCode =
          component.code?.toUpperCase() ||
          component.component_name?.code?.toUpperCase();

        if (componentCode === upperCode) {
          return parseFloat(component.amount) || 0;
        }
      }
    }

    return 0;
  };

  let hasChanges = true;
  let maxIterations = 10;
  let iteration = 0;

  while (hasChanges && iteration < maxIterations) {
    hasChanges = false;
    iteration++;

    for (const [category, components] of Object.entries(currentComponentsMap)) {
      const currentUpdated = updatedCategories[category] || [...components];
      const updatedComponents = [...currentUpdated];
      let categoryChanged = false;

      for (let i = 0; i < updatedComponents.length; i++) {
        const component = updatedComponents[i];
        const componentCode =
          component.code?.toUpperCase() ||
          component.component_name?.code?.toUpperCase();
        const changedCode =
          changedComponent.code?.toUpperCase() ||
          changedComponent.component_name?.code?.toUpperCase();

        if (componentCode === changedCode) {
          continue;
        }

        let newAmount = component.amount || 0;

        if (
          component.computation_method === "Percentage" &&
          component.value_based_on &&
          component.computation_value
        ) {
          const basedOn = Array.isArray(component.value_based_on)
            ? component.value_based_on
            : [component.value_based_on];

          let baseAmount = 0;
          basedOn.forEach((dep) => {
            baseAmount += getComponentValue(dep);
          });

          newAmount =
            (parseFloat(component.computation_value) / 100) * baseAmount;
        }

        if (
          component.computation_method === "Formula" &&
          component.value_based_formula
        ) {
          try {
            let expression = component.value_based_formula
              .toUpperCase()
              .replace(/\s+/g, "");
            const variableValues = {};
            const variableNames = expression.match(/\b[A-Z]+\b/g) || [];
            const uniqueVariables = [...new Set(variableNames)];

            uniqueVariables.forEach((variable) => {
              variableValues[variable] = getComponentValue(variable);
            });

            let evaluatedExpression = expression;
            Object.entries(variableValues).forEach(([key, value]) => {
              const regex = new RegExp(`\\b${key}\\b`, "g");
              evaluatedExpression = evaluatedExpression.replace(
                regex,
                value.toString()
              );
            });

            const calculate = (expr) => {
              while (expr.includes("(")) {
                expr = expr.replace(/\(([^()]+)\)/g, (match, innerExpr) => {
                  return calculate(innerExpr);
                });
              }

              const mulDivRegex =
                /([+-]?\d*\.?\d+)\s*([*/])\s*([+-]?\d*\.?\d+)/;
              while (mulDivRegex.test(expr)) {
                expr = expr.replace(mulDivRegex, (match, a, op, b) => {
                  const numA = parseFloat(a);
                  const numB = parseFloat(b);
                  return op === "*" ? numA * numB : numA / numB;
                });
              }

              const addSubRegex =
                /([+-]?\d*\.?\d+)\s*([+-])\s*([+-]?\d*\.?\d+)/;
              while (addSubRegex.test(expr)) {
                expr = expr.replace(addSubRegex, (match, a, op, b) => {
                  const numA = parseFloat(a);
                  const numB = parseFloat(b);
                  return op === "+" ? numA + numB : numA - numB;
                });
              }

              return parseFloat(expr) || 0;
            };

            newAmount = calculate(evaluatedExpression);
          } catch (error) {
            showToast("error", "Error calculating formula:", error);
            newAmount = 0;
          }
        }

        if (
          (component.computation_method === "Percentage" ||
            component.computation_method === "Formula") &&
          Math.abs(newAmount - (component.amount || 0)) > 0.01
        ) {
          updatedComponents[i] = {
            ...component,
            amount: Number(newAmount.toFixed(2)),
          };
          categoryChanged = true;
          hasChanges = true;
        }
      }

      if (categoryChanged) {
        updatedCategories[category] = updatedComponents;
        currentComponentsMap[category] = updatedComponents;
      }
    }
  }

  return updatedCategories;
};

export const NewSalaryTemplate = ({ onClose, reCallApi, setHanldeLoader }) => {
  const { domain, token, topbarCompanyName, topbarCompanyId } = useSelector(
    (state) => state.tokenReducer
  );
  const [isNetSalaryNegative, setIsNetSalaryNegative] = useState(false);

  const {
    setValue,
    handleSubmit,
    register,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      company_name: topbarCompanyName === "All" ? "" : topbarCompanyName,
      company_id:
        topbarCompanyId === defaultAllSelection ? "" : topbarCompanyId,
      template_name: "",
      salary_type: "",
      salary_basis: "",
      salary: "",
      rounding_rule: "",
      rounding_value: "",
      is_employer_contribution_allowed: false,
      is_reimbursement_allowed: false,
    },
  });

  // Fetch data
  const [isCompanyChanged, setIsCompanyChanged] = useState(false);
  const { result: computationMethodChoices } = useFetch(
    `${domain}HRZone/Salary/Salary_Component/get-computation-method-choices/`
  );

  const { result: componentCategoryData } = useFetch(
    `${domain}hrzone/salary/salary_template/get-salary-type-choices/`
  );

  const { result: roundingRuleData } = useFetch(
    `${domain}hrzone/salary/salary_template/get-rounding-rule-choices/`
  );

  const { result: roundingNumberData } = useFetch(
    `${domain}hrzone/salary/salary_template/get-rounding-number-choices/`
  );

  const { result: salaryBasisData } = useFetch(
    `${domain}hrzone/salary/salary_template/get-salary-basis-choices/`
  );
  const selectedCompanyId = watch("company_id");

  const { result: earningValue, setLoad: recallEarningsApi } = useFetch(
    `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=Earnings&company_id=${selectedCompanyId}`
  );

  const { result: deductionValue, setLoad: recallDeductionApi } = useFetch(
    `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=Deduction&company_id=${selectedCompanyId}`
  );

  const {
    result: employerContributionValue,
    setLoad: recallEmployerContributionApi,
  } = useFetch(
    `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=Employer Contribution&company_id=${selectedCompanyId}`
  );

  const { result: reimbursementValue, setLoad: recallReimbursementApi } =
    useFetch(
      `${domain}HRZone/Salary/Salary_Component/get-component-based-values/?component_category=Reimbursement&company_id=${selectedCompanyId}`
    );

  const totalSalary = parseFloat(watch("salary")) || 0;

  const earnings = useSalaryComponents(
    "earnings",
    earningValue,
    totalSalary,
    isCompanyChanged
  );
  const deductions = useSalaryComponents(
    "deductions",
    deductionValue,
    isCompanyChanged
  );
  const employerContributions = useSalaryComponents(
    "employer_contributions",
    employerContributionValue,
    isCompanyChanged
  );
  const reimbursements = useSalaryComponents(
    "reimbursements",
    reimbursementValue,
    isCompanyChanged
  );

  const totalEarnings = earnings.totalAmount;
  const totalDeductions = deductions.totalAmount;
  const totalEmployerContributions = employerContributions.totalAmount;
  const totalReimbursements = reimbursements.totalAmount;

  let companyDetails = localStorage.getItem("companyAccess")
    ? JSON.parse(localStorage.getItem("companyAccess"))
    : [];

  const salary = watch("salary");

  const netSalary = useMemo(() => {
    return (parseFloat(salary) || 0) - (parseFloat(totalDeductions) || 0);
  }, [salary, totalDeductions]);

  const totalCTC = useMemo(
    () => totalEarnings + totalEmployerContributions,
    [totalEarnings, totalEmployerContributions]
  );

  useEffect(() => {
    if (earnings.components.length > 0) {
      const specialAllowance = earnings.components.find(
        (comp) => comp.isSpecialAllowance
      );

      if (specialAllowance) {
        const totalOtherEarnings = earnings.components.reduce((sum, comp) => {
          if (!comp.isSpecialAllowance && comp.amount) {
            return sum + parseFloat(comp.amount);
          }
          return sum;
        }, 0);

        const newSpecialAllowance = Math.max(
          0,
          totalSalary - totalOtherEarnings
        );

        if (specialAllowance.amount !== newSpecialAllowance) {
          const updatedComponents = [...earnings.components];
          const specialIndex = updatedComponents.findIndex(
            (comp) => comp.isSpecialAllowance
          );

          updatedComponents[specialIndex] = {
            ...updatedComponents[specialIndex],
            amount: newSpecialAllowance,
          };
          earnings.setComponents(updatedComponents);

          setTimeout(() => {
            const allComponentsMap = {
              earnings: updatedComponents,
              deductions: deductions.components,
              employer_contributions: employerContributions.components,
              reimbursements: reimbursements.components,
            };

            const updatedCategories = updateCrossCategoryComponents(
              updatedComponents[specialIndex],
              allComponentsMap,
              totalSalary
            );

            if (updatedCategories.deductions) {
              deductions.setComponents(updatedCategories.deductions);
            }
            if (updatedCategories.employer_contributions) {
              employerContributions.setComponents(
                updatedCategories.employer_contributions
              );
            }
            if (updatedCategories.reimbursements) {
              reimbursements.setComponents(updatedCategories.reimbursements);
            }
          }, 0);
        }
      }
    }
  }, [
    totalSalary,
    earnings,
    deductions,
    employerContributions,
    reimbursements,
  ]);

  const validateForm = async () => {
    const isFormValid = await trigger([
      "template_name",
      "salary_type",
      "effective_from",
      "salary",
      "rounding_rule",
      "rounding_value",
      "net_salary",
    ]);

    const isEarningsValid = earnings.validateComponents();
    const isDeductionsValid = deductions.validateComponents();
    const isEmployerContributionsValid =
      employerContributions.validateComponents();
    const isReimbursementsValid = reimbursements.validateComponents();

    return (
      isFormValid &&
      isEarningsValid &&
      isDeductionsValid &&
      isEmployerContributionsValid &&
      isReimbursementsValid
    );
  };

  const onSubmit = async (data) => {
    const isValid = await validateForm();
    if (!isValid) {
      return;
    }

    if (netSalary < 0) {
      setIsNetSalaryNegative(true);
      return;
    }
    setValue("button_loader", true);
    const requestData = {
      company_id: data?.company_id,
      template_name: data?.template_name,
      salary_type: data?.salary_type,
      salary: data?.salary,
      rounding_rule: data?.rounding_rule,
      rounding_number: data?.rounding_value,
      total_salary: totalEarnings,
      total_deductions: totalDeductions,
      net_salary: netSalary,
      total_employer_contribution: totalEmployerContributions,
      total_ctc: totalCTC,
      total_Reimbursement: totalReimbursements,
      is_employer_contribution: data?.is_employer_contribution_allowed,
      is_reimbursement: data?.is_reimbursement_allowed,
      salary_basis: data?.salary_basis,
      components: [
        earnings.prepareSubmissionData(),
        deductions.prepareSubmissionData(),
        employerContributions.prepareSubmissionData(),
        reimbursements.prepareSubmissionData(),
      ]
        .flat()
        .filter(Boolean),
    };

    const response = await customAPI(
      `${domain}hrzone/salary/salary_template/salary-template-list/`,
      "POST",
      requestData
    );

    if (response.ok) {
      showToast("success", response?.responseDetails?.message);
      setValue("button_loader", false);
      setIsNetSalaryNegative(false);
      onClose();
      setHanldeLoader(false);
      reCallApi(true);
    } else {
      setValue("button_loader", false);
      showToast("error", response?.message);
    }
  };

  const handleCompanyName = (event, data) => {
    if (data) {
      setIsCompanyChanged(true);
      setValue("company_name", data?.label);
      setValue("company_id", data?.id);
      earnings.setComponents([]);
      deductions.setComponents([]);
      employerContributions.setComponents([]);
      reimbursements.setComponents([]);
      setValue("is_employer_contribution_allowed", false);
      setValue("is_reimbursement_allowed", false);
      earnings.setErrors({});
      deductions.setErrors({});
      employerContributions.setErrors({});
      reimbursements.setErrors({});
      recallEarningsApi(true);
      recallDeductionApi(true);
      recallEmployerContributionApi(true);
      recallReimbursementApi(true);
    } else {
      setValue("company_name", "");
      setValue("company_id", "");
      setIsCompanyChanged(false);
    }
  };

  const handleName = (event) => {
    if (event.target.value) {
      setValue("template_name", event.target.value);
    } else {
      setValue("template_name", "");
    }
  };

  const handleSalaryType = (event, data) => {
    if (data) {
      setValue("salary_type", data?.label);
    } else {
      setValue("salary_type", "");
    }
  };

  const handleSalaryBasis = (event, data) => {
    if (data) {
      setValue("salary_basis", data?.label);
    } else {
      setValue("salary_basis", "");
    }
  };

  const handleRoundingRule = (event, data) => {
    if (data) {
      setValue("rounding_rule", data?.label);
    } else {
      setValue("rounding_rule", "");
    }
  };

  const handleRoundingValue = (event, data) => {
    if (data) {
      setValue("rounding_value", data?.label);
    } else {
      setValue("rounding_value", "");
    }
  };

  const handleAmount = (event) => {
    if (
      event.target.value?.length <= 8 &&
      onlyNumberRegex.test(event.target.value)
    ) {
      const newSalary = event.target.value;
      setValue("salary", newSalary);
      setTimeout(() => {
        if (earnings.components?.length > 0) {
          const grossComponent = {
            code: "GROSS",
            component_name: { code: "GROSS" },
            amount: parseFloat(newSalary) || 0,
            computation_method: "Fixed Amount",
          };
          const allComponentsMap = {
            earnings: earnings.components,
            deductions: deductions.components,
            employer_contributions: employerContributions.components,
            reimbursements: reimbursements.components,
          };

          const updatedCategories = updateCrossCategoryComponents(
            grossComponent,
            allComponentsMap,
            parseFloat(newSalary) || 0
          );
          if (updatedCategories.earnings) {
            earnings.setComponents(updatedCategories.earnings);
          }
          if (updatedCategories.deductions) {
            deductions.setComponents(updatedCategories.deductions);
          }
          if (updatedCategories.employer_contributions) {
            employerContributions.setComponents(
              updatedCategories.employer_contributions
            );
          }
          if (updatedCategories.reimbursements) {
            reimbursements.setComponents(updatedCategories.reimbursements);
          }
        }
      }, 100);
    } else {
      setValue("salary", "");
    }
  };

  const handleEarningsChangeWithRecalc = useCallback(
    (index, type, value) => {
      if (type === "name") {
        earnings.handleComponentNameChange(
          index,
          value,
          earnings.setComponents
        );
      } else if (type === "method") {
        earnings.handleComputationMethodChange(
          index,
          value,
          earnings.setComponents
        );
      } else if (type === "amount") {
        earnings.handleAmountChange(index, value, earnings.setComponents);
      }

      setTimeout(() => {
        const changedComponent = earnings.components[index];
        if (changedComponent) {
          const allComponentsMap = {
            earnings: earnings.components,
            deductions: deductions.components,
            employer_contributions: employerContributions.components,
            reimbursements: reimbursements.components,
          };

          const updatedCategories = updateCrossCategoryComponents(
            changedComponent,
            allComponentsMap,
            totalSalary
          );

          if (updatedCategories.deductions) {
            deductions.setComponents(updatedCategories.deductions);
          }
          if (updatedCategories.employer_contributions) {
            employerContributions.setComponents(
              updatedCategories.employer_contributions
            );
          }
          if (updatedCategories.reimbursements) {
            reimbursements.setComponents(updatedCategories.reimbursements);
          }
          if (updatedCategories.earnings) {
            earnings.setComponents(updatedCategories.earnings);
          }
        }
      }, 0);
    },
    [earnings, deductions, employerContributions, reimbursements, totalSalary]
  );

  const handleDeductionsChangeWithRecalc = useCallback(
    (index, type, value) => {
      if (type === "name") {
        deductions.handleComponentNameChange(
          index,
          value,
          deductions.setComponents
        );
      } else if (type === "method") {
        deductions.handleComputationMethodChange(
          index,
          value,
          deductions.setComponents
        );
      } else if (type === "amount") {
        deductions.handleAmountChange(index, value, deductions.setComponents);
      }

      setTimeout(() => {
        const changedComponent = deductions.components[index];
        if (changedComponent) {
          const allComponentsMap = {
            earnings: earnings.components,
            deductions: deductions.components,
            employer_contributions: employerContributions.components,
            reimbursements: reimbursements.components,
          };

          const updatedCategories = updateCrossCategoryComponents(
            changedComponent,
            allComponentsMap,
            totalSalary
          );

          if (updatedCategories.earnings) {
            earnings.setComponents(updatedCategories.earnings);
          }
          if (updatedCategories.employer_contributions) {
            employerContributions.setComponents(
              updatedCategories.employer_contributions
            );
          }
          if (updatedCategories.reimbursements) {
            reimbursements.setComponents(updatedCategories.reimbursements);
          }
        }
      }, 0);
    },
    [deductions, earnings, employerContributions, reimbursements, totalSalary]
  );

  const handleEmployerContributionsChangeWithRecalc = useCallback(
    (index, type, value) => {
      if (type === "name") {
        employerContributions.handleComponentNameChange(
          index,
          value,
          employerContributions.setComponents
        );
      } else if (type === "method") {
        employerContributions.handleComputationMethodChange(
          index,
          value,
          employerContributions.setComponents
        );
      } else if (type === "amount") {
        employerContributions.handleAmountChange(
          index,
          value,
          employerContributions.setComponents
        );
      }

      setTimeout(() => {
        const changedComponent = employerContributions.components[index];
        if (changedComponent) {
          const allComponentsMap = {
            earnings: earnings.components,
            deductions: deductions.components,
            employer_contributions: employerContributions.components,
            reimbursements: reimbursements.components,
          };

          const updatedCategories = updateCrossCategoryComponents(
            changedComponent,
            allComponentsMap,
            totalSalary
          );

          if (updatedCategories.earnings) {
            earnings.setComponents(updatedCategories.earnings);
          }
          if (updatedCategories.deductions) {
            deductions.setComponents(updatedCategories.deductions);
          }
          if (updatedCategories.reimbursements) {
            reimbursements.setComponents(updatedCategories.reimbursements);
          }
        }
      }, 0);
    },
    [employerContributions, earnings, deductions, reimbursements, totalSalary]
  );

  const handleReimbursementsChangeWithRecalc = useCallback(
    (index, type, value) => {
      if (type === "name") {
        reimbursements.handleComponentNameChange(
          index,
          value,
          reimbursements.setComponents
        );
      } else if (type === "method") {
        reimbursements.handleComputationMethodChange(
          index,
          value,
          reimbursements.setComponents
        );
      } else if (type === "amount") {
        reimbursements.handleAmountChange(
          index,
          value,
          reimbursements.setComponents
        );
      }

      setTimeout(() => {
        const changedComponent = reimbursements.components[index];
        if (changedComponent) {
          const allComponentsMap = {
            earnings: earnings.components,
            deductions: deductions.components,
            employer_contributions: employerContributions.components,
            reimbursements: reimbursements.components,
          };

          const updatedCategories = updateCrossCategoryComponents(
            changedComponent,
            allComponentsMap,
            totalSalary
          );

          if (updatedCategories.earnings) {
            earnings.setComponents(updatedCategories.earnings);
          }
          if (updatedCategories.deductions) {
            deductions.setComponents(updatedCategories.deductions);
          }
          if (updatedCategories.employer_contributions) {
            employerContributions.setComponents(
              updatedCategories.employer_contributions
            );
          }
        }
      }, 0);
    },
    [reimbursements, earnings, deductions, employerContributions, totalSalary]
  );

  const buildSpecialAllowanceFormula = (components) => {
    const deductionCodes = components
      .filter((comp) => {
        return (
          !comp.isSpecialAllowance &&
          comp.code &&
          comp.amount !== undefined &&
          comp.amount !== null &&
          comp.amount !== ""
        );
      })
      .map((comp) => comp.code);

    return deductionCodes.length
      ? `GROSS - ${[...deductionCodes].join(" - ")}`
      : "GROSS";
  };

  const handleRemoveComponentWithRecalc = useCallback(
    (category, id, index) => {
      let removedComponent = null;
      let updatedComponents = [];

      if (category === "earnings") {
        removedComponent = earnings.components[index];
        updatedComponents = earnings.components.filter(
          (item, i) => i !== index
        );
        const formula = buildSpecialAllowanceFormula(updatedComponents);
        const updatedComponent = updatedComponents.map((item, i) => {
          if (item?.code === "SP") {
            return {
              ...item,
              value_based_formula: formula,
            };
          }
          return item;
        });
        earnings.setComponents(updatedComponent);
      } else if (category === "deductions") {
        removedComponent = deductions.components[index];
        updatedComponents = deductions.components.filter(
          (item, i) => i !== index
        );
        deductions.setComponents(updatedComponents);
      } else if (category === "employer_contributions") {
        removedComponent = employerContributions.components[index];
        updatedComponents = employerContributions.components.filter(
          (item, i) => i !== index
        );
        employerContributions.setComponents(updatedComponents);
      } else {
        removedComponent = reimbursements.components[index];
        updatedComponents = reimbursements.components.filter(
          (item, i) => i !== index
        );
        reimbursements.setComponents(updatedComponents);
      }

      if (removedComponent) {
        setTimeout(() => {
          const allComponentsMap = {
            earnings:
              category === "earnings" ? updatedComponents : earnings.components,
            deductions:
              category === "deductions"
                ? updatedComponents
                : deductions.components,
            employer_contributions:
              category === "employer_contributions"
                ? updatedComponents
                : employerContributions.components,
            reimbursements:
              category === "reimbursements"
                ? updatedComponents
                : reimbursements.components,
          };

          const updatedCategories = updateCrossCategoryComponents(
            removedComponent,
            allComponentsMap,
            totalSalary
          );

          if (updatedCategories.earnings && category !== "earnings") {
            earnings.setComponents(updatedCategories.earnings);
          }
          if (updatedCategories.deductions && category !== "deductions") {
            deductions.setComponents(updatedCategories.deductions);
          }
          if (
            updatedCategories.employer_contributions &&
            category !== "employer_contributions"
          ) {
            employerContributions.setComponents(
              updatedCategories.employer_contributions
            );
          }
          if (
            updatedCategories.reimbursements &&
            category !== "reimbursements"
          ) {
            reimbursements.setComponents(updatedCategories.reimbursements);
          }
        }, 0);
      }
    },
    [earnings, deductions, employerContributions, reimbursements, totalSalary]
  );

  const updatePercentageDisplay = (component) => {
    if (!component || component.computation_method !== "Percentage") {
      return component;
    }

    const enhanced = { ...component };

    if (enhanced.computation_value && enhanced.value_based_on) {
      const basedOn = Array.isArray(enhanced.value_based_on)
        ? enhanced.value_based_on
        : [enhanced.value_based_on];

      enhanced.value_based_percentage = `${
        enhanced.computation_value
      }% of ${basedOn.join(" + ")}`;
    } else {
      enhanced.value_based_percentage = "";
    }

    return enhanced;
  };

  const handleComponentUpdate = useCallback(
    (category, index, updatedComponent) => {
      let updatedComponents;
      let allComponentsMap;

      const enhancedComponent = updatePercentageDisplay(updatedComponent);

      if (category === "earnings") {
        updatedComponents = [...earnings.components];
        updatedComponents[index] = enhancedComponent;
        earnings.setComponents(updatedComponents);

        allComponentsMap = {
          earnings: updatedComponents,
          deductions: deductions.components,
          employer_contributions: employerContributions.components,
          reimbursements: reimbursements.components,
        };
      } else if (category === "deductions") {
        updatedComponents = [...deductions.components];
        updatedComponents[index] = enhancedComponent;
        deductions.setComponents(updatedComponents);

        allComponentsMap = {
          earnings: earnings.components,
          deductions: updatedComponents,
          employer_contributions: employerContributions.components,
          reimbursements: reimbursements.components,
        };
      } else if (category === "employer_contributions") {
        updatedComponents = [...employerContributions.components];
        updatedComponents[index] = enhancedComponent;
        employerContributions.setComponents(updatedComponents);

        allComponentsMap = {
          earnings: earnings.components,
          deductions: deductions.components,
          employer_contributions: updatedComponents,
          reimbursements: reimbursements.components,
        };
      } else {
        return;
      }

      setTimeout(() => {
        const updatedCategories = updateCrossCategoryComponents(
          updatedComponent,
          allComponentsMap,
          totalSalary
        );

        if (updatedCategories.earnings) {
          earnings.setComponents(updatedCategories.earnings);
        }
        if (updatedCategories.deductions) {
          deductions.setComponents(updatedCategories.deductions);
        }
        if (updatedCategories.employer_contributions) {
          employerContributions.setComponents(
            updatedCategories.employer_contributions
          );
        }
        if (updatedCategories.reimbursements) {
          reimbursements.setComponents(updatedCategories.reimbursements);
        }
      }, 0);
    },
    [earnings, deductions, employerContributions, reimbursements, totalSalary]
  );

  return (
    <Box sx={parentBoxStyle}>
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
        }}
      >
        <Box sx={childBoxStyleWithColor}>
          <Box sx={{ ml: 1 }}>
            <IconButton onClick={onClose} sx={doubleArrowStyle}>
              <DoubleArrow />
            </IconButton>
          </Box>

          <Box sx={{ ml: 2.2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Add Salary Template
            </Typography>
            <Typography {...infoStyle}>
              Create a predefined salary structure that can be applied to
              employees.
            </Typography>
          </Box>
        </Box>
      </Paper>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ flexGrow: 1, overflow: "auto", p: 2 }}>
          <Box sx={childBoxStyle}>
            <Box>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Company Name</Typography>
                <Typography {...infoStyle}>
                  Select the company for which this component appliess
                </Typography>
                <CustomAutoComplete
                  dataList={companyDetails
                    .filter((obj) => obj.company_name !== "All")
                    .map((obj) => ({
                      id: obj.company_id,
                      label: obj.company_name,
                    }))}
                  rules={{
                    ...register("company_name", {
                      required: "Company Name is required",
                    }),
                  }}
                  selectedValue={watch("company_name")}
                  updateDataFunction={handleCompanyName}
                  placeHolder="Select company name"
                  errorMessage={
                    errors?.company_name?.message
                      ? errors?.company_name?.message
                      : ""
                  }
                  textBoxError={errors?.company_name ? true : false}
                />
              </Box>
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>Template Name</Typography>
              <Typography {...infoStyle}>
                Enter a unique name for this salary template.
              </Typography>
              <CustomTextBox
                selectedValue={watch("template_name")}
                rules={{
                  ...register("template_name", {
                    required: "Template name is required",
                    minLength: {
                      value: 3,
                      message: "Minimum length is 3 characters",
                    },
                    maxLength: {
                      value: 100,
                      message: "Maximum length is 100 characters",
                    },
                    validate: {
                      atleastOneCharacterWithSpaceReg: (value) => {
                        if (
                          atleastOneCharacterWithSpace.test(value) ||
                          value === ""
                        ) {
                          return true;
                        } else {
                          return "At least one alphabet required";
                        }
                      },
                      checkShiftUniqueName: async (fieldValue) => {
                        if (fieldValue !== "") {
                          const response = await fetch(
                            `${domain}hrzone/salary/salary_template/check-salary-template-name/?template_name=${fieldValue.trim()}`,
                            {
                              method: "GET",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `token ${token}`,
                              },
                            }
                          );
                          const resp = await response.json();
                          if (resp.exists) {
                            return "Template name already exists";
                          }
                        }
                      },
                    },
                  }),
                }}
                updateDataFunction={handleName}
                placeHolder="Enter Template Name"
                errorMessage={
                  errors?.template_name?.message
                    ? errors?.template_name?.message
                    : ""
                }
                textBoxError={errors.template_name ? true : false}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gap: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  Salary Calculation Type
                </Typography>
                <Typography {...infoStyle} sx={{ mb: 1.5 }}>
                  Choose the type of salary structure.
                </Typography>
                <CustomAutoComplete
                  dataList={componentCategoryData}
                  rules={{
                    ...register("salary_type", {
                      required: "Salary type is required",
                    }),
                  }}
                  selectedValue={watch("salary_type")}
                  updateDataFunction={handleSalaryType}
                  placeHolder="Select salary type"
                  errorMessage={
                    errors?.salary_type?.message
                      ? errors?.salary_type?.message
                      : ""
                  }
                  textBoxError={errors?.salary_type ? true : false}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Salary Basis</Typography>
                <Typography {...infoStyle}>
                  Enter the employee's fixed salary amount
                </Typography>
                <CustomAutoComplete
                  dataList={salaryBasisData
                    .filter((obj) => obj.label === "Gross")
                    .map((obj) => ({
                      ...obj,
                      label: obj.label,
                    }))}
                  rules={{
                    ...register("salary_basis", {
                      required: "Salary basis is required",
                    }),
                  }}
                  selectedValue={watch("salary_basis")}
                  updateDataFunction={handleSalaryBasis}
                  placeHolder="Select salary basis"
                  errorMessage={
                    errors?.salary_basis?.message
                      ? errors?.salary_basis?.message
                      : ""
                  }
                  textBoxError={errors?.salary_basis ? true : false}
                />
              </Box>
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Salary</Typography>
                <Typography {...infoStyle}>
                  Enter the employee's fixed salary amount.
                </Typography>
                <CustomTextBox
                  selectedValue={watch("salary")}
                  rules={{
                    ...register("salary", {
                      required: "Salary is required",
                      validate: (value) => {
                        if (value && parseFloat(value) <= 0) {
                          return "Salary must be greater than 0";
                        }
                        return true;
                      },
                    }),
                  }}
                  updateDataFunction={handleAmount}
                  placeHolder="Enter amount"
                  errorMessage={
                    errors?.salary?.message ? errors?.salary?.message : ""
                  }
                  textBoxError={errors.salary ? true : false}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 2,
              }}
            >
              <Box>
                <Typography sx={{ fontWeight: 600 }}>Rounding Rule</Typography>
                <Typography {...infoStyle}>
                  Choose the rounding rule for salary calculations.
                </Typography>
                <CustomAutoComplete
                  dataList={roundingRuleData}
                  rules={{
                    ...register("rounding_rule", {
                      required: "Rounding rule is required",
                    }),
                  }}
                  selectedValue={watch("rounding_rule")}
                  updateDataFunction={handleRoundingRule}
                  placeHolder="Select rounding rule"
                  errorMessage={
                    errors?.rounding_rule?.message
                      ? errors?.rounding_rule?.message
                      : ""
                  }
                  textBoxError={errors?.rounding_rule ? true : false}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 600 }}>Rounding Value</Typography>
                <Typography {...infoStyle}>
                  Choose the rounding value for salary calculations.
                </Typography>
                <CustomAutoComplete
                  dataList={roundingNumberData}
                  rules={{
                    ...register("rounding_value", {
                      required: "Rounding value is required",
                    }),
                  }}
                  selectedValue={watch("rounding_value")}
                  updateDataFunction={handleRoundingValue}
                  placeHolder="Select rounding value"
                  errorMessage={
                    errors?.rounding_value?.message
                      ? errors?.rounding_value?.message
                      : ""
                  }
                  textBoxError={errors?.rounding_value ? true : false}
                />
              </Box>
            </Box>

            <Divider />

            <SalaryComponentTable
              title="Earnings"
              description="Add components that contribute to employee earnings (Basic, HRA, Bonus, etc.)"
              data={earnings.components}
              earningValue={[
                ...earnings?.components.slice(0, -1),
                { label: "GROSS", code: "GROSS", amount: totalSalary },
              ]}
              is_earning={true}
              setData={earnings.setComponents}
              errors={earnings.errors}
              setErrors={earnings.setErrors}
              componentList={earningValue?.data || []}
              computationMethodChoices={computationMethodChoices || []}
              filteredComponentLists={earnings.filteredComponentLists}
              totalLabel="Total Earnings"
              totalSalary={totalSalary}
              onAddComponent={() => earnings.addComponent()}
              onRemoveComponent={(id, index) =>
                handleRemoveComponentWithRecalc("earnings", id, index)
              }
              onComponentNameChange={(index, value) =>
                handleEarningsChangeWithRecalc(index, "name", value)
              }
              onComputationMethodChange={(index, value) =>
                handleEarningsChangeWithRecalc(index, "method", value)
              }
              onAmountChange={(index, event) =>
                handleEarningsChangeWithRecalc(index, "amount", event)
              }
              onComponentUpdate={(index, updatedComponent) =>
                handleComponentUpdate("earnings", index, updatedComponent)
              }
            />

            <Divider />

            <SalaryComponentTable
              title="Deductions"
              description="Add components that are deducted from earnings (TDS, PF, Loan, etc.)"
              data={deductions.components}
              earningValue={[
                ...earnings?.components,
                { label: "GROSS", code: "GROSS", amount: totalSalary },
              ]}
              setData={deductions.setComponents}
              errors={deductions.errors}
              setErrors={deductions.setErrors}
              componentList={deductionValue?.data || []}
              computationMethodChoices={computationMethodChoices || []}
              filteredComponentLists={deductions.filteredComponentLists}
              totalLabel="Total Deductions"
              totalSalary={totalSalary}
              onAddComponent={() => deductions.addComponent()}
              onRemoveComponent={(id, index) =>
                handleRemoveComponentWithRecalc("deductions", id, index)
              }
              onComponentNameChange={(index, value) =>
                handleDeductionsChangeWithRecalc(index, "name", value)
              }
              onComputationMethodChange={(index, value) =>
                handleDeductionsChangeWithRecalc(index, "method", value)
              }
              onAmountChange={(index, event) =>
                handleDeductionsChangeWithRecalc(index, "amount", event)
              }
              onComponentUpdate={(index, updatedComponent) =>
                handleComponentUpdate("deductions", index, updatedComponent)
              }
            />

            <Divider />

            <Box sx={{ ...childBoxStyleSpaceBetween, px: 0 }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                  Employer Contribution
                </Typography>
                <Typography {...infoStyle}>
                  Include employer contribution component is salary structure
                </Typography>
              </Box>
              <Switch
                checked={watch("is_employer_contribution_allowed")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_employer_contribution_allowed", checked);
                }}
                sx={{ transform: "scale(1.3)" }}
              />
            </Box>

            {watch("is_employer_contribution_allowed") && (
              <SalaryComponentTable
                title="Employer Contribution"
                description="Add statutory or custom employer contributions (PF,ESI,Insurance,etc."
                data={employerContributions.components}
                earningValue={[
                  ...earnings?.components,
                  { label: "GROSS", code: "GROSS", amount: totalSalary },
                ]}
                setData={employerContributions.setComponents}
                errors={employerContributions.errors}
                setErrors={employerContributions.setErrors}
                componentList={employerContributionValue?.data || []}
                computationMethodChoices={computationMethodChoices || []}
                filteredComponentLists={
                  employerContributions.filteredComponentLists
                }
                totalLabel="Total Contribution"
                totalSalary={totalSalary}
                onAddComponent={() => employerContributions.addComponent()}
                onRemoveComponent={(id, index) =>
                  handleRemoveComponentWithRecalc(
                    "employer_contributions",
                    id,
                    index
                  )
                }
                onComponentNameChange={(index, value) =>
                  handleEmployerContributionsChangeWithRecalc(
                    index,
                    "name",
                    value
                  )
                }
                onComputationMethodChange={(index, value) =>
                  handleEmployerContributionsChangeWithRecalc(
                    index,
                    "method",
                    value
                  )
                }
                onAmountChange={(index, event) =>
                  handleEmployerContributionsChangeWithRecalc(
                    index,
                    "amount",
                    event
                  )
                }
                onComponentUpdate={(index, updatedComponent) =>
                  handleComponentUpdate(
                    "employer_contributions",
                    index,
                    updatedComponent
                  )
                }
              />
            )}

            <Divider />

            <Box sx={{ ...childBoxStyleSpaceBetween, px: 0 }}>
              <Box sx={{ display: "flex", flexDirection: "column" }}>
                <Typography sx={{ fontWeight: 600, fontSize: 18 }}>
                  Reimbursement
                </Typography>
                <Typography {...infoStyle}>
                  Include reimbursement components in salary structure
                </Typography>
              </Box>
              <Switch
                checked={watch("is_reimbursement_allowed")}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setValue("is_reimbursement_allowed", checked);
                }}
                sx={{ transform: "scale(1.3)" }}
              />
            </Box>

            {watch("is_reimbursement_allowed") && (
              <SalaryComponentTable
                title="Reimbursement"
                description="Add reimbursement components for employee expenses (Travel,Food,Medical, etc.)"
                data={reimbursements.components}
                earningValue={[
                  ...earnings?.components,
                  { label: "GROSS", code: "GROSS", amount: totalSalary },
                ]}
                setData={reimbursements.setComponents}
                errors={reimbursements.errors}
                setErrors={reimbursements.setErrors}
                componentList={reimbursementValue?.data || []}
                computationMethodChoices={(
                  computationMethodChoices || []
                ).filter((item) => item.label === "Fixed Amount")}
                filteredComponentLists={reimbursements.filteredComponentLists}
                totalLabel="Total Reimbursement"
                onAddComponent={() => reimbursements.addComponent()}
                onRemoveComponent={(id, index) =>
                  handleRemoveComponentWithRecalc("reimbursements", id, index)
                }
                onComponentNameChange={(index, value) =>
                  handleReimbursementsChangeWithRecalc(index, "name", value)
                }
                onComputationMethodChange={(index, value) =>
                  handleReimbursementsChangeWithRecalc(index, "method", value)
                }
                onAmountChange={(index, event) =>
                  handleReimbursementsChangeWithRecalc(index, "amount", event)
                }
                onComponentUpdate={(index, updatedComponent) =>
                  handleComponentUpdate(
                    "reimbursements",
                    index,
                    updatedComponent
                  )
                }
              />
            )}

            <Box
              sx={{
                mt: 3,
                p: 3,
                backgroundColor: "#f8f9fa",
                borderRadius: 2,
                border: "1px solid #e0e0e0",
              }}
            >
              <Typography sx={{ fontWeight: 600 }}>Summary</Typography>
              <Typography {...infoStyle}>
                Add all componets that reduce the employees salary
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell
                        colSpan={2}
                        sx={{ border: "none", py: 1 }}
                      ></TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 800,
                          border: "none",
                          pl: 0,
                          fontSize: "1.2rem",
                          color: "#2e7d32",
                        }}
                      >
                        Total Cost to Company (CTC)
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "none",
                          fontWeight: 800,
                          fontSize: "1.2rem",
                          color: "#2e7d32",
                        }}
                        align="right"
                      >
                        {totalCTC.toFixed(2)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 600, border: "none", pl: 0 }}
                      >
                        Employer Contribution
                      </TableCell>
                      <TableCell sx={{ border: "none" }} align="right">
                        {totalEmployerContributions.toFixed(2)}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 600, border: "none", pl: 0 }}
                      >
                        Gross Salary
                      </TableCell>
                      <TableCell sx={{ border: "none" }} align="right">
                        {totalEarnings.toFixed(2)}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 600, border: "none", pl: 0 }}
                      >
                        Deductions
                      </TableCell>
                      <TableCell sx={{ border: "none" }} align="right">
                        {totalDeductions.toFixed(2)}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={2} sx={{ border: "none", py: 1 }}>
                        <Divider />
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          border: "none",
                          pl: 0,
                          fontSize: "1.1rem",
                        }}
                      >
                        Net Salary
                      </TableCell>
                      <TableCell
                        sx={{
                          border: "none",
                          fontWeight: 700,
                          fontSize: "1.1rem",
                        }}
                        align="right"
                      >
                        {netSalary.toFixed(2)}
                        {isNetSalaryNegative && (
                          <Typography
                            color="error"
                            variant="caption"
                            sx={{ display: "block", mt: 0.5 }}
                          >
                            {"Net salary cannot be negative"}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>

                    <TableRow>
                      <TableCell colSpan={2} sx={{ border: "none", py: 1 }}>
                        <Divider />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box sx={{ ...toAlignEnd, mt: 3 }}>
              <CustomButtonWithBorder
                actionName="Cancel"
                typeName="button"
                actionFuntion={() => onClose()}
              />
              <CustomButton
                actionName="Create"
                typeName="submit"
                disableAction={watch("button_loader")}
                sx={{ ml: 2 }}
              />
            </Box>
          </Box>
        </Box>
      </form>
    </Box>
  );
};
